import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Crown, CalendarClock, RefreshCw, Receipt, Infinity as InfinityIcon } from 'lucide-react';
import { useDashboard } from '@/context/DashboardContext';
import { dashboardApi } from '@/services/dashboardApi';

const PLAN_NAMES = { starter: 'Access', basica: 'PRO', premium: 'Premium' };

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Bloque "Mi membresía" del perfil: plan, renovación/caducidad y baja de la renovación.
export function MembershipStatusCard() {
  const { membership, refreshMembership } = useDashboard();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const plan = membership?.active_membership;
  const expires = fmtDate(membership?.expires_at);
  const cancelled = !!membership?.renewal_cancelled;
  // Vía de pago de la suscripción anual (particular / empresa); upgrades y vitalicia van por la misma
  const billingChannel = membership?.billing?.subscription_channel || null;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.message || okMsg);
      setConfirming(false);
      await refreshMembership?.();
    } catch (e) {
      toast.error(e?.message || 'No se pudo completar la operación');
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
      aria-labelledby="membership-status-title"
      data-testid="membership-status"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center">
            <Crown size={18} className="text-[#c9a84c]" />
          </div>
          <div>
            <h3 id="membership-status-title" className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Mi membresía</h3>
            <p className="text-white text-lg font-semibold">{plan ? (PLAN_NAMES[plan] || plan) : 'Sin membresía activa'}</p>
          </div>
        </div>
        {plan && membership?.is_lifetime && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a84c]/30 bg-[#c9a84c]/10 px-3 py-1 text-xs text-[#c9a84c]">
            <InfinityIcon size={13} /> Vitalicia
          </span>
        )}
      </div>

      {!plan && (
        <p className="mt-4 text-sm text-gray-400">
          Activa tu membresía desde <Link to="/dashboard#membresias" className="text-[#c9a84c] underline underline-offset-2">Inicio</Link> para acceder a la formación, herramientas y comisiones.
        </p>
      )}

      {plan && membership?.is_subscription && (
        <div className="mt-5 space-y-4">
          <div className="flex items-center gap-3 text-sm">
            <CalendarClock size={15} className="text-gray-400" />
            <span className="text-gray-400">{cancelled ? 'Acceso hasta' : 'Próxima renovación'}</span>
            <span className="text-white tabular-nums">{expires || '—'}</span>
          </div>
          {billingChannel && (
            <div className="flex flex-wrap items-start gap-x-3 gap-y-1 text-sm" data-testid="membership-billing">
              <Receipt size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-400">Facturación</span>
              <span className="text-white">
                {billingChannel === 'empresa'
                  ? `Empresa o autónomo · ${membership?.billing?.subscription_entity || 'Inbrindor Tech, S.L.'}`
                  : 'Particular'}
              </span>
            </div>
          )}

          {cancelled ? (
            <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4 text-sm text-amber-100/90">
              Has cancelado la renovación automática. Mantendrás el acceso hasta {expires || 'el final del período pagado'} y no se te cobrará de nuevo.
              <div className="mt-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => dashboardApi.resumeSubscription(), 'Renovación reactivada')}
                  className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/[0.06] disabled:opacity-50"
                  data-testid="membership-resume"
                >
                  <RefreshCw size={13} /> Reactivar renovación
                </button>
              </div>
            </div>
          ) : confirming ? (
            <div className="rounded-xl border border-red-400/20 bg-red-400/[0.05] p-4 text-sm text-gray-300" role="alert">
              ¿Seguro que quieres cancelar la renovación? Conservarás el acceso hasta {expires || 'el final del período pagado'}; después la membresía dejará de estar activa.
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => dashboardApi.cancelSubscription(), 'Renovación cancelada')}
                  className="rounded-lg bg-red-500/80 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-50"
                  data-testid="membership-cancel-confirm"
                >
                  Sí, cancelar renovación
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/[0.06]"
                >
                  Mantener
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="text-xs text-gray-400 underline underline-offset-2 hover:text-white"
              data-testid="membership-cancel"
            >
              Cancelar renovación automática
            </button>
          )}
        </div>
      )}

      {plan && membership?.is_lifetime && (
        <p className="mt-4 text-sm text-gray-400">Tu plaza no caduca ni se renueva: no hay cobros recurrentes.</p>
      )}
    </motion.section>
  );
}

export default MembershipStatusCard;
